import { Injectable } from '@angular/core';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { State } from '@state/core.state';
import { catchError, map, mergeMap, of, forkJoin, firstValueFrom } from 'rxjs';
import { withLatestFrom, take } from 'rxjs/operators';

import {
	RestService,
	ProjectCarrierMapDTO,
	PROJECT_CARRIER_MAP_STATUS,
	StatsDTO,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	CarriersProjectsList,
	TO_DOLLARS,
} from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { EngageCarriersDialogComponent } from '../../engage-carriers-dialog/engage-carriers-dialog.component';

import {
	PROJECT_CARRIER_MAP,
	PROJECT_GROUPS,
	PROJECT_DETAILS,
	RECOMMENDED_CARRIERS,
} from '../actions';
import { projectDetailsPageStateKey, ProjectDetailsState } from '../state';
import { ProjectDetailsEffects } from '../effects/project-details.effects';
import { RecommendedCarriersStore } from '../store/recommended-carriers-store';

@Injectable({
	providedIn: 'root',
})
export class ProjectCarrierMapEffects {
	constructor(
		private actions$: Actions<any>,
		private readonly store: Store<State>,
		private readonly restService: RestService,
		private readonly urlService: UrlService,
		public utilService: UtilService,
		private dialog: MatDialog,
		public cargochief: Cargochief,
		public projectHelperService: ProjectHelperService,
		public readonly projectDetailsEffects: ProjectDetailsEffects,
		private readonly recommendedCarriersStore: RecommendedCarriersStore
	) {}

	getAllProjectCarrierMaps$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.GET_ALL),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				return this.getAllProjectCarrierMaps(projectDetailsPageState.projectId);
			})
		)
	);

	async getAllProjectCarrierMaps(projectId: string) {
		let page = 0,
			pageSize: number = 500,
			totalProjectCarrierMaps!: number,
			projectCarrierMapsList = [],
			totalCalls!: number,
			url;
		do {
			url = this.urlService.getProjectCarrierMap(projectId, page, pageSize);
			const projectCarrierMapsResponse = await this.restService.POST(url, {}).toPromise();
			projectCarrierMapsList = projectCarrierMapsList.concat(projectCarrierMapsResponse.data);
			if (!totalProjectCarrierMaps) {
				totalProjectCarrierMaps = projectCarrierMapsResponse.totalElements;
				totalCalls = Math.round(totalProjectCarrierMaps / pageSize) || 1;
			}
			page += 1;
		} while (page * pageSize < totalProjectCarrierMaps);
		projectCarrierMapsList.forEach((projectCarrierMap: ProjectCarrierMapDTO) => {
			projectCarrierMap.notesCount = projectCarrierMap.carrier?.notesCount ?? 0;
			projectCarrierMap.laneCount = projectCarrierMap.carrier?.laneCount ?? 0;
		});

		const carrierIds = projectCarrierMapsList.map((pcm) => pcm.carrier.id);
		let carriersDataResponse = null;
		if (carrierIds.length) {
			carriersDataResponse = await this.getProjectCarrierMapData(carrierIds);
		}

		if (carriersDataResponse?.data?.length) {
			this.store.dispatch(
				PROJECT_DETAILS.PROJECT.SET_CARRIERS_PROJECTS_LIST({
					list: carriersDataResponse?.data,
				})
			);
			projectCarrierMapsList.forEach((projectCarrierMap: ProjectCarrierMapDTO) => {
				const foundCarrier: CarriersProjectsList = carriersDataResponse?.data.find(
					(cMap: CarriersProjectsList) => {
						return projectCarrierMap.carrier.id === cMap.carrierId;
					}
				);
				if (IS_NOT_NULL_OR_UNDEFINED(foundCarrier)) {
					projectCarrierMap.projectsList = foundCarrier.projectDTOList;
				}
			});
		}

		this.projectDetailsEffects.setProcuredCarriers(projectCarrierMapsList);
		this.projectDetailsEffects.setEngagedCarriers(projectCarrierMapsList);
		this.projectDetailsEffects.setProjectGroups(projectCarrierMapsList);

		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
				list: projectCarrierMapsList,
			})
		);

		// get account carrier lanes
		if (carrierIds.length) {
			this.store.dispatch(PROJECT_CARRIER_MAP.LANES.ACCOUNT_CARRIER.GET({ carrierIds }));
		}
		this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
		return PROJECT_CARRIER_MAP.GET_ALL_SUCCESS({});
	}

	async getProjectCarrierMapData(carrierIds) {
		const projectsForCarriersRequest = {
			carrierIds: carrierIds,
		};
		const projectsForCarriers = this.restService.POST(
			this.urlService.getProjectsByCarrierIdsAndAccountId(),
			projectsForCarriersRequest
		);
		return projectsForCarriers.toPromise();
	}

	getAccountCarrierLanes$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.LANES.ACCOUNT_CARRIER.GET),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const laneId: string = projectDetailsPageState.projectDTO?.laneId;
				if (!laneId) {
					return of();
				}
				let requestDTO = {};
				request[0].carrierIds.forEach((carrierId) => {
					requestDTO[carrierId] = [laneId];
				});
				const url = this.urlService.getAccountCarrierLanes(0, request[0].carrierIds.length);

				return this.restService.POST(url, { carrierIdLaneIdsMap: requestDTO }).pipe(
					map((response: any) => {
						let accountCarrierLanes = {};
						if (response?.data?.length > 0) {
							response.data.map((accountCarrierLane) => {
								accountCarrierLanes[accountCarrierLane.carrierId] = {
									rate: TO_DOLLARS(accountCarrierLane.rate),
									rateUpdatedAt: accountCarrierLane.rateUpdatedAt,
								};
							});
						}
						this.store.dispatch(
							PROJECT_CARRIER_MAP.LANES.CARRIER.GET({ carrierIds: request[0].carrierIds })
						);
						return PROJECT_CARRIER_MAP.LANES.ACCOUNT_CARRIER.SUCCESS({
							accountCarrierLanes: accountCarrierLanes,
						});
					}),
					catchError((error) =>
						of(
							PROJECT_CARRIER_MAP.LANES.ACCOUNT_CARRIER.FAILURE({
								payload: { message: error.error },
							})
						)
					)
				);
			})
		)
	);

	getCarrierLanes$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.LANES.CARRIER.GET),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const laneId: string = projectDetailsPageState.projectDTO?.laneId;
				let requestDTO = {};
				const url = this.urlService.getCarrierLanes(0, request[0].carrierIds.length),
					payload: any = {
						carrierIds: request[0].carrierIds,
						laneId,
					};
				return this.restService.POST(url, payload).pipe(
					map((response: any) => {
						let accountCarrierLanes = Object.assign(
							{},
							projectDetailsPageState.accountCarrierLanes
						);
						if (response.data.length > 0) {
							response.data.map((carrierLane) => {
								if (IS_NOT_NULL_OR_UNDEFINED(carrierLane.rate)) {
									accountCarrierLanes[carrierLane.carrierId] = {
										rate: TO_DOLLARS(carrierLane.rate),
										rateUpdatedAt: carrierLane.rateUpdatedAt,
									};
								}
							});
						}
						return PROJECT_CARRIER_MAP.LANES.CARRIER.SUCCESS({ accountCarrierLanes });
					}),
					catchError((error) =>
						of(PROJECT_CARRIER_MAP.LANES.CARRIER.FAILURE({ payload: { message: error.error } }))
					)
				);
			})
		)
	);

	startRemovingEngagedCarriers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.START),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				if (request[0].projectCarrierMapIDs.length === 1) {
					return of(
						PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.OPEN_DIALOG({
							projectCarrierMapIDs: request[0].projectCarrierMapIDs,
							noteType: request[0].noteType,
						})
					);
				}
				return of(
					PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.DELETE({
						projectCarrierMapIDs: request[0].projectCarrierMapIDs,
					})
				);
			})
		)
	);

	openNotesDialogWhenRemovingEngagedCarrier$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.OPEN_DIALOG),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const projectCarrierMap: ProjectCarrierMapDTO =
					projectDetailsPageState.engagedCarriersList.find(
						(pcm) => pcm.id === request[0].projectCarrierMapIDs[0]
					);
				return this.removeCarrierFromEngaged(
					request[0].projectCarrierMapIDs,
					projectCarrierMap,
					projectDetailsPageState.projectId,
					request[0].noteType
				);
			})
		)
	);

	removeEngagedCarriers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.DELETE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const requestDTO = {
					projectCarrierMapIDs: request[0].projectCarrierMapIDs,
				};
				return this.restService.DELETE(this.urlService.deleteProjectCarrierMap(), requestDTO).pipe(
					map((response: any) => {
						const allProjectCarrierMapsList = [].concat(
							projectDetailsPageState.allProjectCarrierMapsList.filter(
								(pcm) => requestDTO.projectCarrierMapIDs.indexOf(pcm.id) === -1
							)
						);
						this.projectDetailsEffects.setEngagedCarriers(allProjectCarrierMapsList);
						this.store.dispatch(
							PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
								list: allProjectCarrierMapsList,
							})
						);

						// update recommended carriers list
						this.projectDetailsEffects.setCapacitySearchMatchesList(
							projectDetailsPageState.truckSearchResult
						);
						return PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.SUCCESS({});
					}),
					catchError((error) => {
						this.utilService.showSnackbar(
							'Unable to delete selected carriers now. Please try again',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of(
							PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.FAILURE({
								payload: { message: error.error },
							})
						);
					})
				);
			})
		)
	);

	async removeCarrierFromEngaged(
		projectCarrierMapIDs: string[],
		projectCarrierMap: ProjectCarrierMapDTO,
		projectId: string,
		noteType: string
	) {
		let result = await this.dialog
			.open(EngageCarriersDialogComponent, {
				width: '550px',
				panelClass: 'cargo_chief_dialog_container',
				data: {
					carrierIds: [projectCarrierMap.carrier.id],
					noteType: noteType,
					projectId,
					carrierBusinessName: projectCarrierMap?.carrier?.businessName
						? projectCarrierMap.carrier.businessName
						: null,
					feedbackInformation: null,
					showRateForm: false,
					showNotes: true,
					submitButtonText: 'Remove',
					dialogHeader: 'Add note and remove',
				},
			})
			.afterClosed()
			.toPromise();
		if (IS_NOT_NULL_OR_UNDEFINED(result.hideModal)) {
			this.store.dispatch(
				RECOMMENDED_CARRIERS.ENGAGE_WITH_CARRIERS.UPDATE_USER_SETTING({
					updatedSettingValue: result.hideModal,
				})
			);
		}
		return PROJECT_CARRIER_MAP.DELETE_PROJECT_CARRIER_MAP.DELETE({ projectCarrierMapIDs });
	}

	engageToProcure$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.ENGAGE_TO_PROCURE.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let projectCarrierMapsList: ProjectCarrierMapDTO[] = [];
				request[0].projectCarrierMapIds.forEach((selectedPCMId) => {
					const projectCarrierMap: ProjectCarrierMapDTO =
						projectDetailsPageState.engagedCarriersList.find((pcm) => pcm.id === selectedPCMId);
					if (projectCarrierMap) {
						projectCarrierMapsList.push(projectCarrierMap);
					}
				});
				let allProjectCarrierMapsList = projectDetailsPageState.allProjectCarrierMapsList.map(
					(pcm) => Object.assign({}, pcm)
				);
				if (projectCarrierMapsList.length === 1) {
					return this.engageToProcureWithRate(
						projectDetailsPageState,
						projectCarrierMapsList,
						allProjectCarrierMapsList
					);
				}

				return this.moveCarriersToEngagedOrProcured(
					projectDetailsPageState,
					projectCarrierMapsList,
					allProjectCarrierMapsList,
					PROJECT_CARRIER_MAP_STATUS.PROCURED
				);
			})
		)
	);

	async engageToProcureWithRate(
		projectDetailsPageState: ProjectDetailsState,
		projectCarrierMapsList,
		allProjectCarrierMapsList
	) {
		let desiredPrice = null,
			carrierRespondedAt = null;
		let result = await this.projectHelperService.openCarrierRateDialog({
			hasRate: false,
			title: 'Do you have a rate for this?',
		});
		desiredPrice = result && result.desiredPrice;
		carrierRespondedAt = result && result.carrierRespondedAt;
		let payload = {
			...projectCarrierMapsList[0],
			desiredPrice,
			carrierRespondedAt,
		};
		payload['status'] = PROJECT_CARRIER_MAP_STATUS.PROCURED;
		payload.carrierNotAvailable = false;
		const url = this.urlService.submitProjectRate({
			sendNotification: false,
			requesterUserId: null,
		});
		const response = await this.restService.PUT(url, payload).toPromise();
		this.updateAfterEngagedOrProcured(
			allProjectCarrierMapsList,
			projectCarrierMapsList,
			PROJECT_CARRIER_MAP_STATUS.PROCURED
		);
		return this.afterCompletingEngagedOrProcured(
			projectDetailsPageState,
			[response.data],
			`Carriers moved to procured.`,
			PROJECT_CARRIER_MAP_STATUS.PROCURED
		);
	}

	removeProcuredCarriers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.PROCURED.DELETE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const [actionPayload, projectDetailsPageState]: [any, ProjectDetailsState] = request;
				const removedProjectCarrierMapIDs = actionPayload.projectCarrierMapIDs;
				const requestDTO = {
					projectCarrierMapIDs: removedProjectCarrierMapIDs,
				};

				let deleteProjectGroupIds = [];
				// if carrier is not part of system group update, remove carrier from group
				// find group carrier was part of and update it
				const selectedProjectCarrierMap = projectDetailsPageState.allProjectCarrierMapsList.find(
					(pcm) => requestDTO.projectCarrierMapIDs.indexOf(pcm.id) !== -1
				);
				// determine if this group should be  deleted
				if (selectedProjectCarrierMap?.projectGroup) {
					let carrierGroup = projectDetailsPageState.projectGroups.find(
						(group) => group.id === selectedProjectCarrierMap?.projectGroup.id
					);
					const groupPcmIds = carrierGroup.projectCarrierMapsList.map((pcm) => pcm.id);
					const allGroupCarriersSelected = groupPcmIds.every((pcmId) =>
						requestDTO.projectCarrierMapIDs.includes(pcmId)
					);
					if (allGroupCarriersSelected) {
						deleteProjectGroupIds.push(carrierGroup.id);
					}
				}
				return this.restService.DELETE(this.urlService.deleteProjectCarrierMap(), requestDTO).pipe(
					map((response: any) => {
						const allProjectCarrierMapsList = [].concat(
							projectDetailsPageState.allProjectCarrierMapsList.filter(
								(pcm) => requestDTO.projectCarrierMapIDs.indexOf(pcm.id) === -1
							)
						);
						this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
						this.projectDetailsEffects.setProjectGroups(allProjectCarrierMapsList);
						this.store.dispatch(
							PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
								list: allProjectCarrierMapsList,
							})
						);

						// if only system group is left delete it
						this.store
							.select((state) => state[projectDetailsPageStateKey])
							.pipe(take(1))
							.subscribe((state) => {
								const projectGroupsCount = state.projectGroups.length;
								if (projectGroupsCount === 1 && state.projectGroups[0].systemGroup) {
									this.store.dispatch(
										PROJECT_GROUPS.DELETE({
											ids: [state.projectGroups[0].id],
										})
									);
								} else {
									// if all carriers from group are removed delete the group - delete empty groups
									if (deleteProjectGroupIds.length) {
										this.store.dispatch(
											PROJECT_GROUPS.DELETE({
												ids: deleteProjectGroupIds,
											})
										);
									}
								}
							});
						// update recommended carriers list
						const removedCarrierIds = projectDetailsPageState.allProjectCarrierMapsList
							.filter((pcm) => removedProjectCarrierMapIDs.includes(pcm.id))
							.map((pcm) => pcm.carrier.id);

						const allCapacitySearchResults = projectDetailsPageState.allCapacitySearchResults.map(
							(capacitySearchResult) => {
								if (removedCarrierIds.includes(capacitySearchResult.carrierId)) {
									capacitySearchResult.projectCount -= 1;
								}
								return capacitySearchResult;
							}
						);
						this.store.dispatch(
							RECOMMENDED_CARRIERS.SET.ALL_CAPACITY_SEARCH_RESULT({
								list: allCapacitySearchResults,
							})
						);

						const capacitySearchResults = projectDetailsPageState.capacitySearchResults.map(
							(capacitySearchResult) => {
								if (removedCarrierIds.includes(capacitySearchResult.carrierId)) {
									capacitySearchResult.projectCount -= 1;
								}
								return capacitySearchResult;
							}
						);
						this.store.dispatch(
							RECOMMENDED_CARRIERS.SET.CAPACITY_SEARCH_RESULT({
								list: capacitySearchResults,
							})
						);

						this.projectDetailsEffects.setCapacitySearchMatchesList(
							projectDetailsPageState.truckSearchResult
						);
						return PROJECT_CARRIER_MAP.PROCURED.DELETE_SUCCESS({});
					}),
					catchError((error) => {
						this.utilService.showSnackbar(
							'Unable to delete selected carriers now. Please try again',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of(
							PROJECT_CARRIER_MAP.PROCURED.DELETE_FAILURE({
								payload: { message: error.error },
							})
						);
					})
				);
			})
		)
	);

	ProcuredToEngaged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.PROCURED_TO_ENGAGED.CREATE),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				let projectCarrierMapsList: ProjectCarrierMapDTO[] = [];
				request[0].projectCarrierMapIds.forEach((selectedPCMId) => {
					const projectCarrierMap: ProjectCarrierMapDTO =
						projectDetailsPageState.allProjectCarrierMapsList.find(
							(pcm) => pcm.id === selectedPCMId
						);
					if (projectCarrierMap) {
						projectCarrierMapsList.push(projectCarrierMap);
					}
				});
				let allProjectCarrierMapsList = projectDetailsPageState.allProjectCarrierMapsList.map(
					(pcm) => Object.assign({}, pcm)
				);
				return this.moveCarriersToEngagedOrProcured(
					projectDetailsPageState,
					projectCarrierMapsList,
					allProjectCarrierMapsList,
					PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS
				);
			})
		)
	);

	async moveCarriersToEngagedOrProcured(
		projectDetailsPageState: ProjectDetailsState,
		projectCarrierMapsList: ProjectCarrierMapDTO[],
		allProjectCarrierMapsList,
		status: string
	) {
		const url = this.urlService.submitProjectRate({
			sendNotification: false,
			requesterUserId: null,
		});
		let procuredCarriersResponse = [],
			requests = [];
		for (let counter = 0; counter < projectCarrierMapsList.length; counter++) {
			let request = this.restService
				.PUT(
					this.urlService.submitProjectRate({
						sendNotification: false,
						requesterUserId: null,
					}),
					{
						...projectCarrierMapsList[counter],
						desiredPrice: null,
						carrierRespondedAt: null,
						status,
						carrierNotAvailable: false,
					}
				)
				.pipe(
					catchError((error) => {
						this.utilService.showSnackbar(
							'Unable to move carrier to engaged.',
							SNACKBAR.ERROR,
							SNACKBAR.ACTION
						);
						return of({});
					})
				);
			requests.push(request);
		}
		const upateResponse: any = await forkJoin(...requests).toPromise();
		upateResponse.forEach((response) => {
			if (response?.data) {
				procuredCarriersResponse.push(response.data);
			}
		});
		this.updateAfterEngagedOrProcured(allProjectCarrierMapsList, projectCarrierMapsList, status);
		return this.afterCompletingEngagedOrProcured(
			projectDetailsPageState,
			procuredCarriersResponse,
			`Carriers moved to engaged.`,
			status
		);
	}

	updateAfterEngagedOrProcured(
		allProjectCarrierMapsList: ProjectCarrierMapDTO[],
		projectCarrierMapsList: ProjectCarrierMapDTO[],
		status: string
	) {
		let projectCarrierMapIds: string[] = projectCarrierMapsList.map((pcm) => pcm.id);
		allProjectCarrierMapsList.forEach((pcm) => {
			if (projectCarrierMapIds.indexOf(pcm.id) !== -1) {
				pcm.status = status;
				if (status === PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS) {
					pcm.projectGroup = null;
				}
			}
		});
		this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
		this.projectDetailsEffects.setEngagedCarriers(allProjectCarrierMapsList);
		this.projectDetailsEffects.setProjectGroups(allProjectCarrierMapsList);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
				list: allProjectCarrierMapsList,
			})
		);
		this.projectDetailsEffects.setCapacitySearchDataForProjectCarrierMap();
	}

	async afterCompletingEngagedOrProcured(
		projectDetailsPageState: ProjectDetailsState,
		procuredCarriersResponse: ProjectCarrierMapDTO[],
		successMessage: string,
		status: string
	) {
		this.utilService.showSnackbar(successMessage, SNACKBAR.SUCCESS, SNACKBAR.ACTION);
		if (status === PROJECT_CARRIER_MAP_STATUS.PROCURED) {
			this.projectDetailsEffects.moveCarriersToGroupsWhenCarriersProcured(
				projectDetailsPageState,
				procuredCarriersResponse
			);
			return PROJECT_CARRIER_MAP.ENGAGE_TO_PROCURE.SUCCESS({});
		} else {
			// delete empty  groups
			if (
				status === PROJECT_CARRIER_MAP_STATUS.IN_PROGRESS &&
				procuredCarriersResponse[0]?.projectGroup
			) {
				let carrierGroup = projectDetailsPageState.projectGroups.find(
					(group) => group.id === procuredCarriersResponse[0]?.projectGroup.id
				);
				const groupPcmIds = carrierGroup.projectCarrierMapsList.map((pcm) => pcm.id);
				const projectCarrierMapIDs = procuredCarriersResponse.map((pcm) => pcm.id);
				const allGroupCarriersSelected = groupPcmIds.every((pcmId) =>
					projectCarrierMapIDs.includes(pcmId)
				);
				if (allGroupCarriersSelected) {
					this.store.dispatch(
						PROJECT_GROUPS.DELETE({
							ids: [carrierGroup.id],
						})
					);
				}
			}
			return PROJECT_CARRIER_MAP.PROCURED_TO_ENGAGED.SUCCESS({});
		}
	}

	updateCarrierRate$ = createEffect(() =>
		this.actions$.pipe(
			ofType(PROJECT_CARRIER_MAP.UPDATE_RATE.OPEN_DIALOG),
			withLatestFrom(this.store.select(projectDetailsPageStateKey)),
			mergeMap((request: any) => {
				const projectDetailsPageState: ProjectDetailsState = request[1];
				const projectCarrierMap: ProjectCarrierMapDTO = request[0].projectCarrierMap;
				return this.addDesiredRate(
					projectCarrierMap,
					projectDetailsPageState.allProjectCarrierMapsList,
					projectDetailsPageState.accountCarrierLanes
				);
			})
		)
	);

	async addDesiredRate(
		projectCarrierMap: ProjectCarrierMapDTO,
		allProjectCarrierMapsList: ProjectCarrierMapDTO[],
		accountCarrierLanes
	) {
		let result = await this.projectHelperService.openCarrierRateDialog({
			hasRate: true,
			title: 'Update the rate for this Carrier',
		});
		if (IS_NULL_OR_UNDEFINED(result)) {
			return PROJECT_CARRIER_MAP.UPDATE_RATE.CLOSE_DIALOG({});
		}
		const desiredPrice = result.desiredPrice,
			carrierRespondedAt = result.carrierRespondedAt;
		if (IS_NULL_OR_UNDEFINED(desiredPrice) || IS_NULL_OR_UNDEFINED(carrierRespondedAt)) {
			return PROJECT_CARRIER_MAP.UPDATE_RATE.CLOSE_DIALOG({});
		}

		const url = this.urlService.submitProjectRate({
			sendNotification: false,
			requesterUserId: null,
		});
		const payload = {
			...projectCarrierMap,
			desiredPrice,
			carrierRespondedAt,
		};
		payload.carrierNotAvailable = false;
		const response = await this.restService
			.PUT(url, payload)
			.pipe(
				catchError((error) => {
					this.utilService.showSnackbar(
						'Unable to submit rate now. Please try again later.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					return of({});
				})
			)
			.toPromise();
		this.utilService.showSnackbar(
			'Your rate was submitted successfully.',
			SNACKBAR.SUCCESS,
			SNACKBAR.ACTION
		);

		const updatedProjectCarrierMap: ProjectCarrierMapDTO = response.data;
		allProjectCarrierMapsList = allProjectCarrierMapsList.map((pcm) => {
			if (pcm.id == projectCarrierMap.id) {
				let updatedPcm = Object.assign({}, pcm);
				updatedPcm.desiredPrice = updatedProjectCarrierMap.desiredPrice;
				updatedPcm.carrierRespondedAt = updatedProjectCarrierMap.carrierRespondedAt;
				return updatedPcm;
			}
			return pcm;
		});
		this.projectDetailsEffects.setProcuredCarriers(allProjectCarrierMapsList);
		this.projectDetailsEffects.setEngagedCarriers(allProjectCarrierMapsList);
		this.projectDetailsEffects.setProjectGroups(allProjectCarrierMapsList);
		this.store.dispatch(
			PROJECT_CARRIER_MAP.SET.ALL_PROJECT_CARRIER_MAPS_LIST({
				list: allProjectCarrierMapsList,
			})
		);
		return PROJECT_CARRIER_MAP.UPDATE_RATE.SUCCESS({});
	}
}
